import { fetchWithAuth } from "@/lib/http";

export type UserAggregateField =
  | "basic"
  | "profile"
  | "info"
  | "balance"
  | "group"
  | "appkeys"
  | "onmodels";

export type UserBasic = {
  id?: string;
  email?: string;
  nickname?: string;
  [key: string]: unknown;
};

export type UserAggregateResponse = {
  basic?: UserBasic;
  profile?: Record<string, unknown> | undefined;
  info?: Record<string, unknown> | undefined;
  balance?: unknown;
  group?: Record<string, unknown> | undefined;
  appkeys?: unknown[];
  onmodels?: unknown[];
  [key: string]: unknown;
};

export class ApiError extends Error {
  status?: number;
  detail?: string;
  constructor(message: string, status?: number, detail?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

function buildAggregateQuery(fields: UserAggregateField[]) {
  const params = new URLSearchParams();
  fields.forEach(f => params.append("fields", f));
  return params.toString();
}

async function parseError(res: Response) {
  const status = res.status;
  let text = "";
  try {
    text = await res.text();
  } catch {
    text = "";
  }

  const tryParseJson = (raw: string) => {
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };

  const json = tryParseJson(text);
  const detail =
    (json && (json.detail || json.error || json.message)) ||
    (typeof text === "string" && text.trim() ? text.trim() : undefined);
  return new ApiError(
    `请求失败 (${status})`,
    status,
    typeof detail === "string" ? detail : undefined
  );
}

/**
 * 聚合查询当前登录用户信息
 * 示例：fetchUserAggregate(["basic","balance"])
 */
export async function fetchUserAggregate(fields: UserAggregateField[] = ["basic"]) {
  const query = buildAggregateQuery(fields);
  const res = await fetchWithAuth(`/api/user/aggregate?${query}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  if (!res.ok) {
    throw await parseError(res);
  }

  // 后端可能返回对象或数组；将结果解析为 `UserAggregateResponse`，调用方按字段取值
  return (await res.json()) as UserAggregateResponse;
}

export function extractBasicFromAggregate(data: unknown): UserBasic {
  if (!data || typeof data !== "object") return {};
  const obj = data as Record<string, unknown>;
  // 常见结构：{ basic: { id,email,nickname }, ... }
  if (obj.basic && typeof obj.basic === "object") return obj.basic as UserBasic;
  // 兼容直接返回 basic 字段
  return obj as UserBasic;
}
